import { LabelWithDescription } from '@/components/LabelWithdescription';
import {
	LoadingOutlined,
	MinusCircleOutlined,
	PlusOutlined,
	InfoCircleOutlined,
} from '@ant-design/icons';
import { DeleteOutline } from '@mui/icons-material';
import {
	Button,
	Form,
	Input,
	InputNumber,
	notification,
	Select,
	Spin,
	Switch,
	Tooltip,
	Radio,
	Collapse,
} from 'antd';
import React, { useEffect, useState } from 'react';
import { OverViewCardForm } from '../OverviewCardForm';
import { QueryObserverResult, RefetchOptions, useMutation } from '@tanstack/react-query';
import { updateUser } from '@/api/user';
import { User } from '@/types/TAccounts';
import { API_TEMPLATE, API_TEMPLATE_KNOWLEDGE_BASE_LOADER } from './data';
import TextArea from 'antd/es/input/TextArea';
import { API_WORKFLOW_TYPES } from 'src/constants';
import { twillioSystemVariable } from 'src/constants/twillioSystemVariable';
import { closeModal } from '@/store/modal/slice';
import { FormListFieldData, FormListOperation } from 'antd/lib/form/FormList';
import { useDispatch } from 'react-redux';
import { FeatureFlag } from 'src/enums/EFeatureFlags';
import { useFeatureFlag } from 'src/Hooks/useFeatureFlag';
import { analyzeAndUpdateWorkflow } from '@/utils/analysisWorkflow';
import { WorkflowType } from 'src/enums/EWorkflow';
import { getSelectedAgentId } from '@/store/account/selector';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { selectCanAddScenario } from '@/store/scenarioCounts/selector';
import { incrementScenarioCount } from '@/store/scenarioCounts/slice';
import ShortUniqueId from 'short-unique-id';

const uid = new ShortUniqueId({ length: 10 });

export interface CallWebhookWorkflow {
	id: string;
	name: string;
	scenario: string;
	workflowType: number;
	requiresAuthToken?: boolean;
	authApi: {
		url: string;
		method: 'GET' | 'POST';
		headers: { key: string; value: string }[];
		body: string;
		tokenVariable: string;
		tokenPath: string;
		isUrlEncoded: boolean;
	};
	additionalApis?: {
		name: string;
		url: string;
		method: 'GET' | 'POST' | 'PUT';
		headers: { key: string; value: string }[];
		body: string;
		isUrlEncoded: boolean;
		hasCondition?: boolean;
		condition?: {
			sourceApiIndex: number;
			variable: string;
			operator:
				| 'equals'
				| 'notEquals'
				| 'greaterThan'
				| 'lessThan'
				| 'contains'
				| 'notContains'
				| 'exists'
				| 'notExists';
			value?: string;
		};
		responseData: {
			variableName: string;
			jsonPath: string;
			prompt: string;
		}[];
	}[];
	webhookInformation: {
		url: string;
		method: 'GET' | 'POST' | 'PUT' | 'DELETE';
		body: string;
		headers: [];
		responseData: {
			variableName: string;
			jsonPath: any;
			prompt: string;
		}[];
	};
	isQueryParams: boolean;
	isXChecksum?: boolean;
	xChecksumSalt?: string;
	xChecksumAlgorithm?: string;
	xChecksumPayloadSorted?: boolean;
	isURLEncodedApi: boolean;
	extractedCallInfo: {
		name: string;
		type: 'string' | 'number' | 'boolean';
		description: string;
	}[];
	extractFromContext: {
		name: string;
		extractionPrompt: string;
	}[];
	systemVariable: {
		name: string;
		type: string;
	}[];
	timeoutSeconds: number;
	speechToEmit: string;
	isRepetable: boolean;
	postExecutionPrompt?: string;
	availability?: boolean;
	configEnabled?: boolean;
	fallbackMessage?: string;
}

interface ApiWorkflowFormProps {
	apiWorkflowData: {
		call_webhook_workflows: CallWebhookWorkflow[];
	};
	editWorkflowIndex?: number;
	call_webhook_workflows?: {
		call_webhook_workflows: CallWebhookWorkflow[];
	};
	showAddFieldButton?: boolean;
	refetch: (options?: RefetchOptions) => Promise<QueryObserverResult<User, Error>>;
	isDuplicate?: boolean;
}

const ApiWorkflowForm: React.FC<ApiWorkflowFormProps> = ({
	apiWorkflowData,
	call_webhook_workflows,
	editWorkflowIndex = -1,
	showAddFieldButton = true,
	refetch,
	isDuplicate = false,
}) => {
	const [form] = Form.useForm();
	const [isFieldTouchReset, setIsFieldTouchReset] = useState<boolean>(false);
	const [headersEnabled, setHeadersEnabled] = useState<boolean>(true);
	const [bodyEnabled, setBodyEnabled] = useState<boolean>(true);
	const [responseEnabled, setResponseEnabled] = useState<boolean>(true);
	const [workflowType, setWorkflowType] = useState<number>(API_WORKFLOW_TYPES.scenario);
	const [activeCollapseKeys, setActiveCollapseKeys] = useState<{ [key: number]: string[] }>({});

	const isApiTokenIntegrationFlagEnabled = useFeatureFlag(FeatureFlag.API_TOKEN_INTEGRATION);
	const isAPiChainingFlagEnabled = useFeatureFlag(FeatureFlag.API_CHAINING);

	const dispatch = useDispatch();
	const selectedAgentId = useAppSelector(getSelectedAgentId);
	const canAddScenario = useAppSelector(selectCanAddScenario);

	const algorithms = [
		{ label: 'MD5', value: 'md5' },
		{ label: 'SHA-1', value: 'sha1' },
		{ label: 'SHA-224', value: 'sha224' },
		{ label: 'SHA-256', value: 'sha256' },
		{ label: 'SHA-384', value: 'sha384' },
		{ label: 'SHA-512', value: 'sha512' },
		{ label: 'SHA3-224', value: 'sha3_224' },
		{ label: 'SHA3-256', value: 'sha3_256' },
		{ label: 'SHA3-384', value: 'sha3_384' },
		{ label: 'SHA3-512', value: 'sha3_512' },
	];

	useEffect(() => {
		form.resetFields();
		setIsFieldTouchReset(false);

		if (call_webhook_workflows) {
			form.setFieldsValue({
				call_webhook_workflows: call_webhook_workflows.call_webhook_workflows.map(
					(workflow: any) => ({
						...workflow,
						id: workflow.id || uid.rnd(),
						availability: workflow.availability || null,
						configEnabled: workflow.configEnabled || false,
						fallbackMessage: workflow.fallbackMessage || '',
					}),
				),
			});
			const initialType = call_webhook_workflows.call_webhook_workflows[0].workflowType;
			setWorkflowType(initialType); // Set the workflowType state
			setBodyEnabled(!!call_webhook_workflows.call_webhook_workflows[0]?.webhookInformation?.body);
		} else {
			form.setFieldsValue({
				call_webhook_workflows: [
					{
						...(workflowType === API_WORKFLOW_TYPES.scenario
							? API_TEMPLATE
							: API_TEMPLATE_KNOWLEDGE_BASE_LOADER),
						id: uid.rnd(),
					},
				],
			});
		}
	}, [call_webhook_workflows, form]);

	const mutation = useMutation({
		mutationKey: ['update-client'],
		mutationFn: updateUser,
		onSuccess: () => {
			dispatch(incrementScenarioCount(WorkflowType.WEBHOOK));
			refetch();
			notification.success({
				key: 'form-success',
				message: isDuplicate
					? 'API Workflow has been duplicated successfully!'
					: 'API Workflow has been saved successfully!',
				duration: 3,
			});
			form.resetFields();
			setIsFieldTouchReset(true);
			form.setFieldsValue({
				call_webhook_workflows: [
					{
						...(workflowType === API_WORKFLOW_TYPES.scenario
							? API_TEMPLATE
							: API_TEMPLATE_KNOWLEDGE_BASE_LOADER),
						id: uid.rnd(),
					},
				],
			});
			dispatch(closeModal());
		},
	});

	const handleMethodChange = (value: 'GET' | 'POST' | 'PUT' | 'DELETE', name: any) => {
		setBodyEnabled(value === 'POST' || value === 'PUT');

		// Update form state when HTTP method changes
		form.setFields([
			{
				name: [name, 'webhookInformation', 'method'],
				value,
			},
		]);
	};

	const handleTypeChange = (value: number, name: any) => {
		setWorkflowType(value);

		// Get current values from form
		const currentWorkflows = [...form.getFieldValue('call_webhook_workflows')];

		// Update the specific workflow
		if (currentWorkflows[name]) {
			currentWorkflows[name] = {
				...currentWorkflows[name],
				workflowType: value,
				...(value === API_WORKFLOW_TYPES.knowledgeBaseLoader ? { scenario: null } : {}),
			};

			// Update form with new values
			form.setFieldsValue({
				call_webhook_workflows: currentWorkflows,
			});
		}
	};

	const handleSwitchChange = (checked: boolean, index: number, fieldName: string) => {
		try {
			const formValues = form.getFieldValue('call_webhook_workflows');
			if (!formValues || !Array.isArray(formValues)) {
				notification.error({
					message: 'Form values are not properly initialized',
				});
				return;
			}

			const updatedWorkflows = formValues.map((workflow: any, i: number) => {
				if (i === index) {
					// Ensure webhookInformation exists
					const updatedWebhookInfo = workflow?.webhookInformation || {};
					switch (fieldName) {
						case 'headers':
							setHeadersEnabled(checked);
							updatedWebhookInfo.headers = checked ? [] : undefined;
							break;
						case 'responseData':
							setResponseEnabled(checked);
							updatedWebhookInfo.responseData = checked ? [] : undefined;
							break;
						case 'body':
							setBodyEnabled(checked);
							updatedWebhookInfo.body = checked ? updatedWebhookInfo.body || '' : undefined;
							break;
						default:
							break;
					}
					return {
						...workflow,
						webhookInformation: updatedWebhookInfo,
					};
				}
				return workflow;
			});

			// Update form state immediately and mark as touched
			form.setFields([
				{
					name: ['call_webhook_workflows'],
					value: updatedWorkflows,
					touched: true,
				},
			]);

			// Ensure collapse stays open for body toggle if needed
			if (fieldName === 'body' && checked && !activeCollapseKeys[index]?.includes('1')) {
				setActiveCollapseKeys((prev) => ({
					...prev,
					[index]: [...(prev[index] || []), '1'],
				}));
			}

			// Trigger isFieldTouchReset to true
			setIsFieldTouchReset(true);
		} catch (error) {
			notification.error({
				message: 'An error occurred while updating the form',
				description: error instanceof Error ? error.message : 'Unknown error occurred',
			});
		}
	};

	const handleSubmit = async (values: any) => {
		try {
			const updatedValues = { ...values };
			updatedValues.call_webhook_workflows = updatedValues.call_webhook_workflows.map(
				(workflow: any) => {
					let updatedWorkflow = {
						...workflow,
						id: workflow.id ?? uid.rnd(),
					};

					if (workflowType === API_WORKFLOW_TYPES.knowledgeBaseLoader) {
						updatedWorkflow = {
							...updatedWorkflow,
							extractedCallInfo: [],
							responseEnabled: false,
							bodyEnabled: false,
						};
					}

					// Process body enabled
					if (!bodyEnabled) {
						updatedWorkflow = {
							...updatedWorkflow,
							webhookInformation: {
								...updatedWorkflow.webhookInformation,
								body: undefined,
							},
						};
					}

					// Process auth token
					if (updatedWorkflow.requiresAuthToken) {
						updatedWorkflow.webhookInformation.authResponseData = [
							{
								variableName: updatedWorkflow.authApi.tokenVariable,
								jsonPath: updatedWorkflow.authApi.tokenPath,
							},
						];
					}

					// Process additional APIs
					if (updatedWorkflow.additionalApis && updatedWorkflow.additionalApis.length > 0) {
						// Ensure all additional APIs have the necessary fields
						updatedWorkflow.additionalApis = updatedWorkflow.additionalApis.map((api: any) => ({
							...api,
							headers: api.headers || [],
							responseData: api.responseData || [],
							...(api.hasCondition ? {} : { condition: undefined }),
							...(api.condition &&
							(api.condition.operator === 'exists' || api.condition.operator === 'notExists')
								? { condition: { ...api.condition, value: undefined } }
								: {}),
						}));
					}

					return updatedWorkflow;
				},
			);

			// Rest of the submit function remains the same
			let newWorkflows: CallWebhookWorkflow[] = [...apiWorkflowData.call_webhook_workflows];

			if (showAddFieldButton || isDuplicate) {
				newWorkflows = [
					...apiWorkflowData.call_webhook_workflows,
					...updatedValues.call_webhook_workflows,
				];
			} else {
				newWorkflows = apiWorkflowData.call_webhook_workflows.map((item, index) =>
					index === editWorkflowIndex ? updatedValues.call_webhook_workflows[0] : item,
				);
			}

			await mutation.mutateAsync({
				call_webhook_workflows: JSON.stringify(newWorkflows),
				agentId: selectedAgentId as string,
			});

			form.resetFields;
			await analyzeAndUpdateWorkflow(
				{ workflows: newWorkflows },
				WorkflowType.WEBHOOK,
				dispatch,
				selectedAgentId as string,
			);
		} catch (error) {
			console.error('Error submitting form:', error);
		}
	};

	const handleAddApi = (add: () => void) => {
		if (!canAddScenario) {
			notification.error({
				message: 'Scenario limit reached',
				description: 'Max scenarios (100) reached. Please Delete existing to add more.',
			});
			return;
		}
		add();
	};

	// Helper function to return a conditional required validator in access Token fields.
	const conditionalRequired = (index: number, fieldName = 'Required') => ({
		validator: (_: any, value: any) => {
			const requiresToken = form.getFieldValue([
				'call_webhook_workflows',
				index,
				'requiresAuthToken',
			]);
			if (!requiresToken || (requiresToken && value)) {
				return Promise.resolve();
			}
			return Promise.reject(new Error(fieldName));
		},
	});

	const ColumnHeader = ({ title, tooltip }: { title: string; tooltip: string }) => (
		<div className="flex items-center mb-2">
			<span className="font-medium mr-1">{title}</span>
			<Tooltip title={tooltip}>
				<InfoCircleOutlined className="text-blue-500 cursor-help" />
			</Tooltip>
		</div>
	);

	const FormItems = () => (
		<>
			<Form.List
				key={call_webhook_workflows?.call_webhook_workflows[0]?.id}
				name="call_webhook_workflows"
				initialValue={[
					{
						...(workflowType === API_WORKFLOW_TYPES.scenario
							? API_TEMPLATE
							: API_TEMPLATE_KNOWLEDGE_BASE_LOADER),
						id: uid.rnd(),
					},
				]}
			>
				{(fields, { add, remove }) => (
					<>
						{fields.map(({ key, name, ...restField }, index) => {
							return (
								<div key={key} className="mb-8 bg-white rounded-lg shadow-sm">
									<div className="p-4">
										{!isDuplicate ? (
											<div className="flex justify-between items-center">
												<span className="text-lg font-semibold">API Workflow {index + 1}</span>
												{fields.length > 1 && showAddFieldButton && (
													<Button
														type="primary"
														danger
														icon={<DeleteOutline />}
														onClick={() => remove(name)}
													>
														Delete
													</Button>
												)}
											</div>
										) : (
											<div className="flex justify-start">
												<span className="text-lg font-semibold">Customize Your Duplicate</span>
											</div>
										)}

										{/* Workflow Name */}
										<div className="my-4">
											<LabelWithDescription
												label={
													<div className="flex items-center">
														<span className="text-sm font-semibold">Workflow Name</span>
														<span className="text-red-500 ml-1">*</span>
													</div>
												}
												customClassName="text-sm"
												description="Give this workflow a clear, descriptive name that explains what it does (e.g., 'Check Account Balance', 'Get Weather Info')."
											/>
											<Form.Item
												{...restField}
												name={[name, 'name']}
												rules={[{ required: true, message: 'Please enter a workflow name' }]}
											>
												<Input
													placeholder="e.g., Check Customer Account Balance"
													className="w-full"
												/>
											</Form.Item>
										</div>

										<div className="my-4">
											<LabelWithDescription
												label={
													<div className="flex items-center">
														<span className="text-sm font-semibold">Workflow Type</span>
														<span className="text-red-500 ml-1">*</span>
													</div>
												}
												customClassName="text-sm"
												description={
													workflowType === API_WORKFLOW_TYPES.scenario
														? 'Scenario allows you to trigger API workflows based on specific conversation contexts or user requests'
														: 'Knowledge Base Loader allows you to dynamically fetch and use external data sources to enhance AI responses'
												}
											/>
											<div className="flex">
												<button
													type="button"
													className={`w-1/2 py-2 px-4 text-sm font-medium rounded-l-md ${
														workflowType === API_WORKFLOW_TYPES.scenario
															? 'bg-blue-600 text-white'
															: 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
													}`}
													onClick={() => handleTypeChange(API_WORKFLOW_TYPES.scenario, index)}
												>
													Scenario
												</button>
												<button
													type="button"
													className={`w-1/2 py-2 px-4 text-sm font-medium rounded-r-md ${
														workflowType === API_WORKFLOW_TYPES.knowledgeBaseLoader
															? 'bg-blue-600 text-white'
															: 'bg-white text-gray-700 border border-l-0 border-gray-300 hover:bg-gray-50'
													}`}
													onClick={() =>
														handleTypeChange(API_WORKFLOW_TYPES.knowledgeBaseLoader, index)
													}
												>
													Knowledge Base Loader
												</button>
											</div>
										</div>

										{/* Workflow Trigger Scenario */}
										{workflowType !== API_WORKFLOW_TYPES.knowledgeBaseLoader && (
											<div className="my-4">
												<LabelWithDescription
													label={
														<div className="flex items-center">
															<span className="text-sm font-semibold">
																When should this trigger?
															</span>
														</div>
													}
													customClassName="text-sm"
													description="Describe when the AI should use this workflow. Be specific about what the customer might say or ask for."
												/>
												<Form.Item
													{...restField}
													name={[name, 'scenario']}
													rules={[
														{
															required: workflowType !== API_WORKFLOW_TYPES.knowledgeBaseLoader,
															message: 'Please enter a workflow trigger scenario.',
														},
													]}
												>
													<Input
														placeholder="e.g., When the customer asks about their account balance, billing information, or wants to check their payment history"
														className="w-full"
													/>
												</Form.Item>
											</div>
										)}

										{/* Knowledge Base Loader Prompt */}
										{workflowType === API_WORKFLOW_TYPES.knowledgeBaseLoader && (
											<div className="my-4">
												<LabelWithDescription
													label="Prompt"
													description="Specify what to instruct your AI before providing the loaded knowledge base."
												/>

												<span className="mb-4">
													<Form.Item className="flex-auto !mb-0" name={[name, 'prompt']}>
														<Input placeholder="e.g., Here is the new updated pricing for products in dynamic category, which changes every hour." />
													</Form.Item>
												</span>
											</div>
										)}

										{/* Repeatable Switch */}
										{workflowType !== API_WORKFLOW_TYPES.knowledgeBaseLoader && (
											<div className="my-4">
												<LabelWithDescription
													label={
														<div className="flex items-center">
															<span className="text-sm font-semibold">Repeatable</span>
														</div>
													}
													customClassName="text-sm"
													description="Specify if the workflow can trigger multiple times over a single call."
												/>
												<Form.Item
													{...restField}
													name={[name, 'isRepeatable']}
													valuePropName="checked"
												>
													<Switch className="mb-2" />
												</Form.Item>
											</div>
										)}

										{/* Query Params */}
										<div className="my-4">
											<LabelWithDescription
												label={
													<div className="flex items-center">
														<span className="text-sm font-semibold">Query Params</span>
													</div>
												}
												customClassName="text-sm"
												description="Use Collected Information as Query Params."
											/>
											<Form.Item
												{...restField}
												name={[name, 'isQueryParams']}
												valuePropName="checked"
											>
												<Switch className="mb-2" />
											</Form.Item>
										</div>

										{/* Query Params */}
										<div className="my-4">
											<LabelWithDescription
												label={
													<div className="flex items-center">
														<span className="text-sm font-semibold">URL Encoded</span>
													</div>
												}
												customClassName="text-sm"
												description="Send request body data in a URL encoded format instead of JSON."
											/>
											<Form.Item
												{...restField}
												name={[name, 'isURLEncodedApi']}
												valuePropName="checked"
											>
												<Switch className="mb-2" />
											</Form.Item>
										</div>

										{/* X-Checksum */}
										<div className="my-4">
											<LabelWithDescription
												label={
													<div className="flex items-center">
														<span className="text-sm font-semibold">
															Secured request with X-Checksum
														</span>
													</div>
												}
												customClassName="text-sm"
												description={
													<div className="flex flex-col items-start">
														<span className="font-bold">X-Checksum Header</span>
														<span>
															We send a secured request by validating that the data hasn't been
															altered or tampered with, using a server-side checksum attached as an
															X-Checksum header generated from the provided salt or hash key.
														</span>

														<span className="font-bold mt-4">Supported X-Checksum:</span>
														<span>• body only</span>
														<span>• body + query</span>
														<span>• params only</span>
													</div>
												}
											/>
											<Form.Item
												{...restField}
												name={[name, 'isXChecksum']}
												valuePropName="checked"
											>
												<Switch />
											</Form.Item>
											<Form.Item
												noStyle
												shouldUpdate={(prevValues, currentValues) => {
													const prev = prevValues?.call_webhook_workflows?.[name]?.isXChecksum;
													const current =
														currentValues?.call_webhook_workflows?.[name]?.isXChecksum;
													return prev !== current;
												}}
											>
												{({ getFieldValue }) => {
													const isXChecksum = getFieldValue([
														'call_webhook_workflows',
														name,
														'isXChecksum',
													]);
													return (
														isXChecksum && (
															<div className="flex items-center gap-4 w-full">
																<Form.Item
																	{...restField}
																	name={[name, 'xChecksumSalt']}
																	label="Salt"
																	className="w-full"
																>
																	<Input.Password
																		className="w-full"
																		visibilityToggle
																		placeholder="Place your Salt or Hashed Password"
																	/>
																</Form.Item>
																<Form.Item
																	className="w-full"
																	label="Algorithm"
																	{...restField}
																	name={[name, 'xChecksumAlgorithm']}
																	initialValue="sha256"
																>
																	<Select>
																		{algorithms.map((item) => {
																			return (
																				<Select.Option key={item.value}>{item.label}</Select.Option>
																			);
																		})}
																	</Select>
																</Form.Item>
																<Form.Item
																	className="w-full"
																	label="Sort Req Payload?"
																	{...restField}
																	name={[name, 'xChecksumPayloadSorted']}
																	valuePropName="checked"
																>
																	<Switch />
																</Form.Item>
															</div>
														)
													);
												}}
											</Form.Item>
										</div>

										{isApiTokenIntegrationFlagEnabled ? (
											<Form.Item
												noStyle
												shouldUpdate={(prev: any, curr: any) =>
													prev.call_webhook_workflows[index]?.requiresAuthToken !==
													curr.call_webhook_workflows[index]?.requiresAuthToken
												}
											>
												{({ getFieldValue }) => {
													const requiresAuthToken = getFieldValue([
														'call_webhook_workflows',
														index,
														'requiresAuthToken',
													]);

													return (
														<div className="mb-8">
															<div className="bg-gray-50 py-2 px-5 rounded-lg border border-gray-200">
																<div className="flex justify-between items-center">
																	<div className="flex items-center">
																		<div className="text-blue-600 mr-2">
																			<svg
																				xmlns="http://www.w3.org/2000/svg"
																				className="h-5 w-5"
																				viewBox="0 0 20 20"
																				fill="currentColor"
																			>
																				<path
																					fillRule="evenodd"
																					d="M18 8a6 6 0 01-7.743 5.743L10 14l-1 1-1 1H6v-1l1-1 1-1-.257-.257A6 6 0 1118 8zm-6-4a1 1 0 100 2h2a1 1 0 100-2h-2z"
																					clipRule="evenodd"
																				/>
																			</svg>
																		</div>
																		<h2 className="text-lg font-medium text-gray-700">
																			Authentication
																		</h2>
																	</div>

																	<Form.Item
																		noStyle
																		name={[name, 'requiresAuthToken']}
																		valuePropName="checked"
																	>
																		<Switch />
																	</Form.Item>
																</div>

																{requiresAuthToken && (
																	<div className="my-4 bg-white p-5 rounded-lg border border-gray-200">
																		{/* Authentication API URL */}
																		<label className="block text-sm font-medium text-gray-700 mb-1">
																			URL <span className="text-red-500">*</span>
																		</label>
																		<Form.Item
																			name={[name, 'authApi', 'url']}
																			rules={[conditionalRequired(index, 'Required')]}
																		>
																			<Input placeholder="https://auth.api.com/token" />
																		</Form.Item>

																		{/* Authentication Method */}
																		<label className="block text-sm font-medium text-gray-700 mb-1">
																			Method <span className="text-red-500">*</span>
																		</label>
																		<Form.Item
																			name={[name, 'authApi', 'method']}
																			rules={[conditionalRequired(index, 'Required')]}
																		>
																			<Radio.Group
																				onChange={(e) => handleMethodChange(e.target.value, name)}
																				className="flex space-x-4"
																			>
																				<Radio value="GET">GET</Radio>
																				<Radio value="POST">POST</Radio>
																			</Radio.Group>
																		</Form.Item>

																		{/* Query Params */}
																		<div className="flex items-center justify-between mb-4">
																			<LabelWithDescription
																				label={
																					<div className="flex items-center">
																						<span className="text-sm">URL Encoded</span>
																					</div>
																				}
																				customClassName="text-sm"
																				description="Use URL-encoded format (application/x-www-form-urlencoded) for the request body."
																			/>
																			<Form.Item
																				name={[name, 'authApi', 'isUrlEncoded']}
																				valuePropName="checked"
																				className="mb-0"
																			>
																				<Switch className="mb-2" defaultChecked={false} />
																			</Form.Item>
																		</div>

																		{/* Authentication Body */}
																		<label className="block text-sm font-medium text-gray-700 mb-1">
																			Request Body <span className="text-red-500">*</span>
																		</label>
																		<Form.Item
																			name={[name, 'authApi', 'body']}
																			rules={[conditionalRequired(index, 'Required')]}
																		>
																			<TextArea placeholder='{"client_id": "xyz", "client_secret": "abc"}' />
																		</Form.Item>

																		{/* Authentication Headers */}
																		<label className="block text-sm font-medium text-gray-700 mb-1">
																			Header <span className="text-red-500">*</span>
																		</label>
																		<Form.List name={[name, 'authApi', 'headers']}>
																			{(fields, { add, remove }) => {
																				return (
																					<>
																						{fields.map(
																							({ key: hKey, name: hName, ...hRestField }) => (
																								<div
																									key={hKey}
																									className="grid grid-cols-[1fr,1fr,auto] gap-4 items-center mb-2"
																								>
																									<Form.Item
																										{...hRestField}
																										name={[hName, 'key']}
																										rules={[conditionalRequired(index, 'Required')]}
																										className="mb-0"
																									>
																										<Input placeholder="Header Name" />
																									</Form.Item>
																									<Form.Item
																										{...hRestField}
																										name={[hName, 'value']}
																										rules={[conditionalRequired(index, 'Required')]}
																										className="mb-0"
																									>
																										<Input placeholder="Header Value" />
																									</Form.Item>
																									{fields.length > 1 && (
																										<MinusCircleOutlined
																											onClick={() => remove(hName)}
																											className="ml-2 flex-shrink-0 mb-0"
																										/>
																									)}
																								</div>
																							),
																						)}
																						<Form.Item>
																							<Button
																								type="dashed"
																								onClick={() => add()}
																								block
																								icon={<PlusOutlined />}
																							>
																								Add Header
																							</Button>
																						</Form.Item>
																					</>
																				);
																			}}
																		</Form.List>

																		<div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
																			{/* Token Variable */}
																			<div>
																				<label className="block text-sm font-medium text-gray-700 mb-1">
																					Token Variable Name{' '}
																					<span className="text-red-500">*</span>
																				</label>
																				<Form.Item
																					name={[name, 'authApi', 'tokenVariable']}
																					rules={[conditionalRequired(index, 'Required')]}
																				>
																					<Input placeholder="e.g., accessToken" />
																				</Form.Item>
																			</div>

																			{/* Token JSON Path */}
																			<div>
																				<label className="block text-sm font-medium text-gray-700 mb-1">
																					API Response Token <span className="text-red-500">*</span>
																				</label>
																				<Form.Item
																					name={[name, 'authApi', 'tokenPath']}
																					rules={[conditionalRequired(index, 'Required')]}
																				>
																					<Input placeholder="$.access_token" />
																				</Form.Item>
																			</div>
																		</div>
																	</div>
																)}
															</div>
														</div>
													);
												}}
											</Form.Item>
										) : (
											''
										)}

										{/* Additional APIs Section */}
										{isAPiChainingFlagEnabled && (
											<div className="mb-8">
												<div className="bg-gray-50 py-2 px-5 rounded-lg border border-gray-200">
													<div className="flex justify-between items-center">
														<div className="flex items-center">
															<div className="text-blue-600 mr-2">
																<svg
																	xmlns="http://www.w3.org/2000/svg"
																	className="h-5 w-5"
																	viewBox="0 0 20 20"
																	fill="currentColor"
																>
																	<path
																		fillRule="evenodd"
																		d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z"
																		clipRule="evenodd"
																	/>
																</svg>
															</div>
															<div>
																<h2 className="text-lg font-medium text-gray-700 mb-0">
																	Chained APIs (Optional)
																</h2>
																<p className="text-sm text-gray-500 mt-1">
																	If you would like to call multiple APIs before the main API
																</p>
															</div>
														</div>
													</div>

													<Form.List name={[name, 'additionalApis']}>
														{(apiFields, { add: addApi, remove: removeApi }) => (
															<>
																{apiFields.map((apiField, apiIndex) => (
																	<div
																		key={apiField.key}
																		className="mt-4 bg-white p-5 rounded-lg border border-gray-200"
																	>
																		<div className="flex justify-between items-center mb-4">
																			<h3 className="text-md font-medium">API {apiIndex + 1}</h3>
																			<Button
																				type="text"
																				danger
																				icon={<MinusCircleOutlined />}
																				onClick={() => removeApi(apiField.name)}
																			>
																				Remove
																			</Button>
																		</div>

																		{/* API Name */}
																		<div className="mb-4">
																			<label className="block text-sm font-medium text-gray-700 mb-1">
																				API Name <span className="text-red-500">*</span>
																			</label>
																			<Form.Item
																				name={[apiField.name, 'name']}
																				rules={[
																					{ required: true, message: 'API name is required' },
																				]}
																			>
																				<Input placeholder="e.g., Get User Details" />
																			</Form.Item>
																		</div>

																		{/* API URL */}
																		<div className="mb-4">
																			<label className="block text-sm font-medium text-gray-700 mb-1">
																				URL <span className="text-red-500">*</span>
																			</label>
																			<Form.Item
																				name={[apiField.name, 'url']}
																				rules={[{ required: true, message: 'URL is required' }]}
																			>
																				<Input placeholder="https://api.example.com/endpoint" />
																			</Form.Item>
																		</div>

																		{/* API Method */}
																		<div className="mb-4">
																			<label className="block text-sm font-medium text-gray-700 mb-1">
																				HTTP Method <span className="text-red-500">*</span>
																			</label>
																			<Form.Item
																				name={[apiField.name, 'method']}
																				rules={[{ required: true, message: 'Method is required' }]}
																			>
																				<Radio.Group className="flex space-x-4">
																					<Radio value="GET">GET</Radio>
																					<Radio value="POST">POST</Radio>
																					<Radio value="PUT">PUT</Radio>
																				</Radio.Group>
																			</Form.Item>
																		</div>

																		{/* URL Encoded */}
																		<div className="flex items-center justify-between mb-4">
																			<LabelWithDescription
																				label={<span className="text-sm">URL Encoded</span>}
																				customClassName="text-sm"
																				description="Use URL-encoded format for the request body"
																			/>
																			<Form.Item
																				name={[apiField.name, 'isUrlEncoded']}
																				valuePropName="checked"
																				className="mb-0"
																			>
																				<Switch className="mb-2" defaultChecked={false} />
																			</Form.Item>
																		</div>

																		{/* API Headers */}
																		<div className="mb-4">
																			<label className="block text-sm font-medium text-gray-700 mb-1">
																				Headers
																			</label>
																			<Form.List name={[apiField.name, 'headers']}>
																				{(
																					headerFields,
																					{ add: addHeader, remove: removeHeader },
																				) => (
																					<>
																						{headerFields.map((headerField) => (
																							<div
																								key={headerField.key}
																								className="grid grid-cols-[1fr,1fr,auto] gap-4 items-center mb-2"
																							>
																								<Form.Item
																									name={[headerField.name, 'key']}
																									rules={[{ required: true, message: 'Required' }]}
																									className="mb-0"
																								>
																									<Input placeholder="Header Name" />
																								</Form.Item>
																								<Form.Item
																									name={[headerField.name, 'value']}
																									rules={[{ required: true, message: 'Required' }]}
																									className="mb-0"
																								>
																									<Input placeholder="Header Value" />
																								</Form.Item>
																								<MinusCircleOutlined
																									onClick={() => removeHeader(headerField.name)}
																									className="ml-2 flex-shrink-0"
																								/>
																							</div>
																						))}
																						<Form.Item>
																							<Button
																								type="dashed"
																								onClick={() => addHeader()}
																								block
																								icon={<PlusOutlined />}
																							>
																								Add Header
																							</Button>
																						</Form.Item>
																					</>
																				)}
																			</Form.List>
																		</div>

																		{/* API Body */}
																		<div className="mb-4">
																			<label className="block text-sm font-medium text-gray-700 mb-1">
																				Request Body
																			</label>
																			<Form.Item name={[apiField.name, 'body']}>
																				<TextArea
																					placeholder={'{"userId": "{{user_id}}", "details": true}'}
																					autoSize={{ minRows: 3, maxRows: 6 }}
																				/>
																			</Form.Item>
																		</div>

																		{/* Conditional Execution */}
																		<div className="mb-4">
																			<div className="flex items-center justify-between">
																				<LabelWithDescription
																					label={
																						<span className="text-sm font-medium">
																							Conditional Execution
																						</span>
																					}
																					customClassName="text-sm"
																					description="Execute this API only if a specific condition is met"
																				/>
																				<Form.Item
																					name={[apiField.name, 'hasCondition']}
																					valuePropName="checked"
																					className="mb-0"
																				>
																					<Switch className="mb-2" />
																				</Form.Item>
																			</div>

																			<Form.Item
																				noStyle
																				shouldUpdate={(prev, curr) => {
																					return (
																						prev?.call_webhook_workflows?.[name]?.additionalApis?.[
																							apiField.name
																						]?.hasCondition !==
																						curr?.call_webhook_workflows?.[name]?.additionalApis?.[
																							apiField.name
																						]?.hasCondition
																					);
																				}}
																			>
																				{({ getFieldValue }) => {
																					const hasCondition = getFieldValue([
																						'call_webhook_workflows',
																						name,
																						'additionalApis',
																						apiField.name,
																						'hasCondition',
																					]);

																					return hasCondition ? (
																						<div className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
																							<h4 className="text-sm font-medium mb-3">
																								Define Condition
																							</h4>

																							{/* Source API Selection */}
																							<div className="mb-3">
																								<label className="block text-sm text-gray-700 mb-1">
																									Source API <span className="text-red-500">*</span>
																								</label>
																								<Form.Item
																									name={[
																										apiField.name,
																										'condition',
																										'sourceApiIndex',
																									]}
																									rules={[
																										{
																											required: hasCondition,
																											message: 'Please select source API',
																										},
																									]}
																								>
																									<Select placeholder="Select source API">
																										{Array.from({ length: apiIndex }).map(
																											(_, idx) => {
																												const apiName =
																													getFieldValue([
																														'call_webhook_workflows',
																														name,
																														'additionalApis',
																														idx,
																														'name',
																													]) || `API ${idx + 1}`;
																												return (
																													<Select.Option key={idx} value={idx}>
																														{apiName}
																													</Select.Option>
																												);
																											},
																										)}
																										{apiIndex === 0 && (
																											<Select.Option value="auth" disabled>
																												Add an API before this one to create
																												conditions
																											</Select.Option>
																										)}
																									</Select>
																								</Form.Item>
																							</div>

																							{/* Variable Selection */}
																							<div className="mb-3">
																								<label className="block text-sm text-gray-700 mb-1">
																									Variable <span className="text-red-500">*</span>
																								</label>
																								<Form.Item
																									name={[apiField.name, 'condition', 'variable']}
																									rules={[
																										{
																											required: hasCondition,
																											message: 'Please enter variable name',
																										},
																									]}
																								>
																									<Input placeholder="e.g., user_status, $.data.active" />
																								</Form.Item>
																								<p className="text-xs text-gray-500 mt-1">
																									Enter the variable name from previous API response
																									or use JSONPath syntax
																								</p>
																							</div>

																							{/* Condition Operator */}
																							<div className="mb-3">
																								<label className="block text-sm font-medium text-gray-700 mb-1">
																									Operator <span className="text-red-500">*</span>
																								</label>
																								<Form.Item
																									name={[apiField.name, 'condition', 'operator']}
																									rules={[
																										{
																											required: hasCondition,
																											message: 'Please select an operator',
																										},
																									]}
																								>
																									<Select placeholder="Select operator">
																										<Select.Option value="equals">
																											equals (==)
																										</Select.Option>
																										<Select.Option value="notEquals">
																											not equals (!=)
																										</Select.Option>
																										<Select.Option value="greaterThan">
																											greater than (&gt;)
																										</Select.Option>
																										<Select.Option value="lessThan">
																											less than (&lt;)
																										</Select.Option>
																										<Select.Option value="contains">
																											contains
																										</Select.Option>
																										<Select.Option value="notContains">
																											does not contain
																										</Select.Option>
																										<Select.Option value="exists">
																											exists
																										</Select.Option>
																										<Select.Option value="notExists">
																											does not exist
																										</Select.Option>
																									</Select>
																								</Form.Item>
																							</div>

																							{/* Comparison Value */}
																							<Form.Item
																								noStyle
																								shouldUpdate={(prev, curr) => {
																									return (
																										prev?.call_webhook_workflows?.[name]
																											?.additionalApis?.[apiField.name]?.condition
																											?.operator !==
																										curr?.call_webhook_workflows?.[name]
																											?.additionalApis?.[apiField.name]?.condition
																											?.operator
																									);
																								}}
																							>
																								{({ getFieldValue }) => {
																									const operator = getFieldValue([
																										'call_webhook_workflows',
																										name,
																										'additionalApis',
																										apiField.name,
																										'condition',
																										'operator',
																									]);

																									// Don't show value field for exists/notExists operators
																									if (
																										operator === 'exists' ||
																										operator === 'notExists'
																									) {
																										return null;
																									}

																									return (
																										<div className="mb-3">
																											<label className="block text-sm text-gray-700 mb-1">
																												Value{' '}
																												<span className="text-red-500">*</span>
																											</label>
																											<Form.Item
																												name={[apiField.name, 'condition', 'value']}
																												rules={[
																													{
																														required:
																															hasCondition &&
																															operator !== 'exists' &&
																															operator !== 'notExists',
																														message: 'Please enter a value',
																													},
																												]}
																											>
																												<Input placeholder="e.g., active, true, 100" />
																											</Form.Item>
																										</div>
																									);
																								}}
																							</Form.Item>
																						</div>
																					) : null;
																				}}
																			</Form.Item>
																		</div>

																		{/* Response Mapping */}
																		<div className="mb-4">
																			<label className="block text-sm font-medium text-gray-700 mb-1">
																				Response Data Mapping
																			</label>
																			<Form.List name={[apiField.name, 'responseData']}>
																				{(
																					responseFields,
																					{ add: addResponse, remove: removeResponse },
																				) => (
																					<>
																						{responseFields.map((responseField) => (
																							<div
																								key={responseField.key}
																								className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 items-center"
																							>
																								<Form.Item
																									name={[responseField.name, 'variableName']}
																									rules={[{ required: true, message: 'Required' }]}
																									className="mb-0"
																								>
																									<Input placeholder="Variable Name (e.g., user_data)" />
																								</Form.Item>
																								<div className="flex items-center">
																									<Form.Item
																										name={[responseField.name, 'jsonPath']}
																										rules={[
																											{ required: true, message: 'Required' },
																										]}
																										className="mb-0 flex-grow"
																									>
																										<Input placeholder="JSON Path (e.g., $.data.user)" />
																									</Form.Item>
																									<MinusCircleOutlined
																										onClick={() =>
																											removeResponse(responseField.name)
																										}
																										className="ml-2 flex-shrink-0"
																									/>
																								</div>
																							</div>
																						))}
																						<Form.Item>
																							<Button
																								type="dashed"
																								onClick={() => addResponse()}
																								block
																								icon={<PlusOutlined />}
																							>
																								Add Response Mapping
																							</Button>
																						</Form.Item>
																					</>
																				)}
																			</Form.List>
																		</div>
																	</div>
																))}

																<Form.Item className="mt-4">
																	<Button
																		type="dashed"
																		onClick={() => addApi()}
																		block
																		icon={<PlusOutlined />}
																	>
																		Add Another API
																	</Button>
																</Form.Item>
															</>
														)}
													</Form.List>
												</div>
											</div>
										)}

										{/* Main API Request section */}
										<div className="flex items-center mb-4">
											<div className="text-blue-600 mr-2">
												<svg
													xmlns="http://www.w3.org/2000/svg"
													className="h-5 w-5"
													viewBox="0 0 20 20"
													fill="currentColor"
												>
													<path
														fillRule="evenodd"
														d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z"
														clipRule="evenodd"
													/>
												</svg>
											</div>
											<h2 className="text-lg font-medium text-gray-700 mb-0">Main API Request</h2>
										</div>
										<div className="my-4">
											<LabelWithDescription
												label={
													<div className="flex items-center">
														<span className="text-sm font-semibold">Request URL / Address</span>
														<span className="text-red-500 ml-1">*</span>
													</div>
												}
												customClassName="text-sm"
												description="Enter the complete web address (URL) where your API can be reached. This is usually provided by your API provider or in your API documentation."
											/>
											<div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
												<p className="text-sm text-amber-800 mb-1">
													<strong>💡 Pro Tip:</strong> You can use variables in your URL too!
												</p>
												<p className="text-xs text-amber-700">
													For path parameters:{' '}
													<code className="bg-amber-100 px-1 rounded">
														https://api.example.com/users/{'{{user_id}}'}
													</code>
													<br />
													For query parameters:{' '}
													<code className="bg-amber-100 px-1 rounded">
														https://api.example.com/search?q={'{{search_term}}'}
													</code>
												</p>
											</div>
											<span className="mb-4">
												<Form.Item
													className="flex-auto !mb-0"
													name={[name, 'webhookInformation', 'url']}
													rules={[{ required: true, message: 'Missing URL.' }]}
												>
													<Input
														type="url"
														placeholder="https://api.example.com/users/{{user_id}}"
													/>
												</Form.Item>
											</span>
										</div>

										{/* Request Method */}
										<div className="my-4">
											<LabelWithDescription
												label={
													<div className="flex items-center">
														<span className="text-sm font-semibold">Request Method</span>
														<span className="text-red-500 ml-1">*</span>
													</div>
												}
												customClassName="text-sm"
												description="Choose the HTTP method your API requires. GET is for retrieving data, POST is for sending data. Check your API documentation if unsure - most APIs specify which method to use."
											/>
											<Form.Item
												{...restField}
												name={[name, 'webhookInformation', 'method']}
												rules={[{ required: true, message: 'Please select a request method' }]}
											>
												<Radio.Group
													onChange={(e) => handleMethodChange(e.target.value, name)}
													className="flex space-x-4"
												>
													<Radio value="GET">GET</Radio>
													<Radio value="POST">POST</Radio>
													<Radio value="PUT">PUT</Radio>
													<Radio value="DELETE">DELETE</Radio>
												</Radio.Group>
											</Form.Item>
										</div>

										{/* Request Headers */}
										<Collapse
											defaultActiveKey={['1']}
											className="mb-4 border border-gray-200 rounded-lg overflow-hidden"
										>
											<Collapse.Panel
												header={
													<div className="flex items-center">
														<span className="text-sm font-semibold">Headers</span>
													</div>
												}
												key="1"
											>
												<Switch
													checked={headersEnabled}
													onChange={(checked) => handleSwitchChange(checked, index, 'headers')}
													className="mb-2"
												/>
												{headersEnabled && (
													<>
														<div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-2">
															<ColumnHeader
																title="Header Name"
																tooltip="The name of the HTTP header"
															/>
															<ColumnHeader
																title="Header Value"
																tooltip="The value of the HTTP header"
															/>
														</div>
														<Form.List name={[name, 'webhookInformation', 'headers']}>
															{(headerFields, { add: addHeader, remove: removeHeader }) => {
																return (
																	<>
																		{headerFields.map(
																			({ key: hKey, name: hName, ...hRestField }) => (
																				<div
																					key={hKey}
																					className="grid grid-cols-[1fr,1fr,auto] gap-4 items-center mb-2"
																				>
																					<Form.Item
																						{...hRestField}
																						name={[hName, 'key']}
																						rules={[{ required: true, message: 'Required' }]}
																						className="mb-0"
																					>
																						<Input placeholder="Header Name" />
																					</Form.Item>
																					<Form.Item
																						{...hRestField}
																						name={[hName, 'value']}
																						rules={[{ required: true, message: 'Required' }]}
																						className="mb-0"
																					>
																						<Input placeholder="Header Value" />
																					</Form.Item>
																					{headerFields.length > 1 && (
																						<MinusCircleOutlined
																							onClick={() => removeHeader(hName)}
																							className="ml-2 flex-shrink-0 mb-3"
																						/>
																					)}
																				</div>
																			),
																		)}
																		<Form.Item>
																			<Button
																				type="dashed"
																				onClick={() => addHeader()}
																				block
																				icon={<PlusOutlined />}
																			>
																				Add Header
																			</Button>
																		</Form.Item>
																	</>
																);
															}}
														</Form.List>
													</>
												)}
											</Collapse.Panel>
										</Collapse>

										{workflowType !== API_WORKFLOW_TYPES.knowledgeBaseLoader && (
											<>
												{/* Information to Collect */}
												<Collapse
													defaultActiveKey={[]}
													className="mb-4 border border-gray-200 rounded-lg overflow-hidden"
												>
													<Collapse.Panel
														header={
															<div className="flex items-center">
																<span className="text-sm font-semibold">
																	Required Information from Customer
																</span>
															</div>
														}
														key="1"
													>
														<div className="p-4">
															<div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
																<p className="text-sm text-green-800 mb-1">
																	<strong>✅ Remember:</strong> After configuring these variables,
																	use them in your API URL or request body!
																</p>
																<p className="text-xs text-green-700">
																	Variable "account_number" becomes{' '}
																	<code className="bg-green-100 px-1 rounded">
																		{'{{account_number}}'}
																	</code>{' '}
																	in your API request
																</p>
															</div>
															<div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-2">
																<ColumnHeader
																	title="Variable Name"
																	tooltip="The name of the variable to be used in the API request"
																/>
																<ColumnHeader
																	title="Type"
																	tooltip="The data type of the variable"
																/>
																<ColumnHeader
																	title="Question to Ask"
																	tooltip="The question the AI will ask to collect this information"
																/>
															</div>
															<Form.List name={[name, 'extractedCallInfo']} initialValue={[{}]}>
																{(
																	fields: FormListFieldData[],
																	{ add, remove }: FormListOperation,
																	meta: { errors: React.ReactNode[] },
																) => {
																	const nodes = (
																		<>
																			{fields.map(({ key, name: fieldName, ...restField }) => (
																				<div
																					key={key}
																					className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 items-center"
																				>
																					<Form.Item
																						{...restField}
																						name={[fieldName, 'name']}
																						className="mb-0"
																					>
																						<Input placeholder="e.g., account_number" />
																					</Form.Item>
																					<Form.Item
																						{...restField}
																						name={[fieldName, 'type']}
																						className="mb-0"
																					>
																						<Select
																							options={[
																								{ value: 'string', label: 'Text' },
																								{ value: 'number', label: 'Number' },
																								{ value: 'boolean', label: 'Yes/No' },
																							]}
																						/>
																					</Form.Item>
																					<div className="flex items-center">
																						<Form.Item
																							{...restField}
																							name={[fieldName, 'description']}
																							className="mb-0 flex-grow"
																						>
																							<Input placeholder="e.g., What's your account number?" />
																						</Form.Item>
																						<MinusCircleOutlined
																							onClick={() => remove(fieldName)}
																							className="ml-2 flex-shrink-0"
																						/>
																					</div>
																				</div>
																			))}
																			<Form.Item>
																				<Button
																					type="dashed"
																					onClick={() => add()}
																					block
																					icon={<PlusOutlined />}
																				>
																					Add Another Question
																				</Button>
																			</Form.Item>
																			<Form.ErrorList errors={meta.errors} />
																		</>
																	);
																	return nodes;
																}}
															</Form.List>
														</div>
													</Collapse.Panel>
												</Collapse>

												{/* Extract from Previous Context */}
												<Collapse
													defaultActiveKey={[]}
													className="mb-4 border border-gray-200 rounded-lg overflow-hidden"
												>
													<Collapse.Panel
														header={
															<div className="flex items-center">
																<span className="text-sm font-semibold">
																	Optional Information (Use if Available)
																</span>
															</div>
														}
														key="1"
													>
														<div className="p-4">
															<div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-4">
																<p className="text-sm text-purple-800 mb-1">
																	<strong>🔍 Context Variables:</strong> These are automatically
																	extracted but still need to be used in your API!
																</p>
																<p className="text-xs text-purple-700">
																	These variables also need{' '}
																	<code className="bg-purple-100 px-1 rounded">
																		{'{{variable_name}}'}
																	</code>{' '}
																	syntax in your URL/body
																</p>
															</div>
															<div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
																<ColumnHeader
																	title="Variable Name"
																	tooltip="Name of the variable to use in your API request"
																/>
																<ColumnHeader
																	title="How to Extract"
																	tooltip="Specify what information to look for from the earlier conversation"
																/>
															</div>
															<Form.List name={[name, 'extractFromContext']}>
																{(
																	fields: FormListFieldData[],
																	{ add, remove }: FormListOperation,
																	meta: { errors: React.ReactNode[] },
																) => {
																	const nodes = (
																		<>
																			{fields.map(({ key, name: fieldName, ...restField }) => (
																				<div
																					key={key}
																					className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 items-center"
																				>
																					<Form.Item
																						{...restField}
																						name={[fieldName, 'name']}
																						className="mb-0"
																						rules={[
																							{
																								required: true,
																								message: 'Variable name is required',
																							},
																						]}
																					>
																						<Input placeholder="e.g., booking_id, is_existing_customer, selected_service" />
																					</Form.Item>
																					<div className="flex items-center">
																						<Form.Item
																							{...restField}
																							name={[fieldName, 'extractionPrompt']}
																							className="mb-0 flex-grow"
																							rules={[
																								{
																									required: true,
																									message: 'Extraction prompt is required',
																								},
																							]}
																						>
																							<Input placeholder="e.g., Get the booking ID that was provided when the user scheduled their appointment earlier in the conversation" />
																						</Form.Item>
																						<MinusCircleOutlined
																							onClick={() => remove(fieldName)}
																							className="ml-2 flex-shrink-0"
																						/>
																					</div>
																				</div>
																			))}
																			<Form.Item>
																				<Button
																					type="dashed"
																					onClick={() => add()}
																					block
																					icon={<PlusOutlined />}
																				>
																					Add Optional Information
																				</Button>
																			</Form.Item>
																			<Form.ErrorList errors={meta.errors} />
																		</>
																	);
																	return nodes;
																}}
															</Form.List>
														</div>
													</Collapse.Panel>
												</Collapse>

												{/* Extract from system variable */}
												<Collapse
													defaultActiveKey={[]}
													className="mb-4 border border-gray-200 rounded-lg overflow-hidden"
												>
													<Collapse.Panel
														header={
															<div className="flex items-center">
																<span className="text-sm font-semibold">
																	Use System Information
																</span>
															</div>
														}
														key="1"
													>
														<div className="p-4">
															<div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
																<p className="text-sm text-yellow-800 mb-1">
																	<strong>⚡ System Variables:</strong> Access call metadata and
																	system information automatically!
																</p>
																<p className="text-xs text-yellow-700">
																	These extract data like caller phone number, call timestamp, etc.
																	Also use{' '}
																	<code className="bg-yellow-100 px-1 rounded">
																		{'{{variable_name}}'}
																	</code>{' '}
																	syntax in your API
																</p>
															</div>
															<div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
																<ColumnHeader
																	title="Variable Name"
																	tooltip="Name of the variable to use in your API request"
																/>
																<ColumnHeader
																	title="System Variable"
																	tooltip="Select what system information to automatically include"
																/>
															</div>
															<Form.List name={[name, 'systemVariable']}>
																{(
																	fields: FormListFieldData[],
																	{ add, remove }: FormListOperation,
																	meta: { errors: React.ReactNode[] },
																) => {
																	const nodes = (
																		<>
																			{fields.map(({ key, name: fieldName, ...restField }) => (
																				<div
																					key={key}
																					className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 items-center"
																				>
																					<Form.Item
																						{...restField}
																						name={[fieldName, 'name']}
																						className="mb-0"
																					>
																						<Input placeholder="e.g., phone, country, city" />
																					</Form.Item>
																					<div className="flex items-center">
																						<Form.Item
																							{...restField}
																							name={[fieldName, 'type']}
																							className="mb-0 flex-grow"
																						>
																							<Select placeholder="Select a system variable">
																								{twillioSystemVariable.map((variable) => (
																									<Select.Option
																										key={variable.value}
																										value={variable.value}
																									>
																										{variable.label}
																									</Select.Option>
																								))}
																							</Select>
																						</Form.Item>
																						<MinusCircleOutlined
																							onClick={() => remove(fieldName)}
																							className="ml-2 flex-shrink-0"
																						/>
																					</div>
																				</div>
																			))}
																			<Form.Item>
																				<Button
																					type="dashed"
																					onClick={() => add()}
																					block
																					icon={<PlusOutlined />}
																				>
																					Add System Variable
																				</Button>
																			</Form.Item>
																			<Form.ErrorList errors={meta.errors} />
																		</>
																	);
																	return nodes;
																}}
															</Form.List>
														</div>
													</Collapse.Panel>
												</Collapse>

												{/* Request Body */}
												<Collapse
													defaultActiveKey={['1']}
													className="mb-4 border border-gray-200 rounded-lg overflow-hidden"
												>
													<Collapse.Panel
														header={
															<div className="flex items-center">
																<span className="text-sm font-semibold">Request Body</span>
															</div>
														}
														key="1"
														collapsible="header"
													>
														<div>
															<Switch
																checked={bodyEnabled}
																onChange={(checked) => handleSwitchChange(checked, index, 'body')}
																className="mb-2"
															/>
															{bodyEnabled && (
																<div>
																	<div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
																		<div className="flex items-start">
																			<div className="flex-shrink-0 mr-2">
																				<svg
																					className="w-5 h-5 text-blue-600 mt-0.5"
																					fill="currentColor"
																					viewBox="0 0 20 20"
																				>
																					<path
																						fillRule="evenodd"
																						d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
																						clipRule="evenodd"
																					/>
																				</svg>
																			</div>
																			<div>
																				<h4 className="text-sm font-medium text-blue-800 mb-1">
																					💡 How to Use Your Variables
																				</h4>
																				<p className="text-sm text-blue-700 mb-2">
																					Use your configured variables in this request body with
																					double curly braces:{' '}
																					<code className="bg-blue-100 px-1 rounded">
																						{'{{variableName}}'}
																					</code>
																				</p>
																				<p className="text-xs text-blue-600">
																					<strong>Example:</strong> If you configured a variable
																					named "account_number", use it here as{' '}
																					<code className="bg-blue-100 px-1 rounded">
																						{'{{account_number}}'}
																					</code>
																				</p>
																			</div>
																		</div>
																	</div>
																	<Form.Item
																		name={[name, 'webhookInformation', 'body']}
																		rules={[{ required: true, message: 'Missing request body' }]}
																	>
																		<TextArea
																			placeholder={
																				'{\n  "category": "MacBook",\n  "hard_disk": "{{hardDisk}}",\n  "processor": {\n    "cpu_core": "{{core}}"\n  },\n  "customer_email": "{{email}}"\n}'
																			}
																			autoSize={{ minRows: 6, maxRows: 10 }}
																		/>
																	</Form.Item>
																</div>
															)}
														</div>
													</Collapse.Panel>
												</Collapse>

												{/* Response */}
												<Collapse
													defaultActiveKey={[]}
													className="mb-4 border border-gray-200 rounded-lg overflow-hidden"
												>
													<Collapse.Panel
														header={
															<div className="flex items-center">
																<span className="text-sm font-semibold">Response</span>
															</div>
														}
														key="1"
													>
														<LabelWithDescription
															label={
																<div className="flex items-center">
																	<span className="text-sm font-semibold">
																		Extract Data from API Response
																	</span>
																</div>
															}
															customClassName="text-sm"
															description="Specify which data to extract from the API response."
														/>
														<Switch
															checked={responseEnabled}
															onChange={(checked) =>
																handleSwitchChange(checked, index, 'responseData')
															}
															className="mb-2"
														/>
														{responseEnabled && (
															<>
																<div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-2">
																	<ColumnHeader
																		title="Variable Name"
																		tooltip="The name of the variable to store the extracted data"
																	/>
																	<ColumnHeader
																		title="JSON Path"
																		tooltip="The JSON path to extract the specific data from the API response"
																	/>
																	<ColumnHeader
																		title="AI Instruction"
																		tooltip="How the AI should interpret and use this data in the conversation"
																	/>
																</div>
																<Form.List name={[name, 'webhookInformation', 'responseData']}>
																	{(
																		responseFields,
																		{ add: addResponse, remove: removeResponse },
																	) => (
																		<>
																			{responseFields.map(
																				({ key: rKey, name: rName, ...rRestField }) => (
																					<div
																						key={rKey}
																						className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 items-center"
																					>
																						<Form.Item
																							{...rRestField}
																							name={[rName, 'variableName']}
																							rules={[{ required: true, message: 'Required' }]}
																							className="mb-0"
																						>
																							<Input placeholder="e.g., temperature" />
																						</Form.Item>
																						<Form.Item
																							{...rRestField}
																							name={[rName, 'jsonPath']}
																							rules={[{ required: true, message: 'Required' }]}
																							className="mb-0"
																						>
																							<Input placeholder="e.g., $.main.temp" />
																						</Form.Item>
																						<div className="flex items-center">
																							<Form.Item
																								{...rRestField}
																								name={[rName, 'prompt']}
																								rules={[{ required: true, message: 'Required' }]}
																								className="mb-0 flex-grow"
																							>
																								<Input placeholder="e.g., The temperature is {{temperature}}" />
																							</Form.Item>
																							{responseFields.length > 1 && (
																								<MinusCircleOutlined
																									onClick={() => removeResponse(rName)}
																									className="ml-2 flex-shrink-0"
																								/>
																							)}
																						</div>
																					</div>
																				),
																			)}
																			<Form.Item>
																				<Button
																					type="dashed"
																					onClick={() => addResponse()}
																					block
																					icon={<PlusOutlined />}
																				>
																					Add Response Mapping
																				</Button>
																			</Form.Item>
																		</>
																	)}
																</Form.List>
															</>
														)}
													</Collapse.Panel>
												</Collapse>
												<div className="my-4">
													<LabelWithDescription
														label={
															<div className="flex items-center">
																<span className="text-sm font-semibold">Speech to Emit</span>
															</div>
														}
														customClassName="text-sm"
														description="Specify what you want the AI to say when the process of API communication starts."
													/>

													<span className="mb-4">
														<Form.Item className="flex-auto !mb-0" name={[name, 'speechToEmit']}>
															<Input placeholder="Please wait a moment while I process your request." />
														</Form.Item>
													</span>
												</div>

												{/* Post-Execution Prompt Field */}
												<div className="mb-8">
													<LabelWithDescription
														label="What should the AI do next?"
														description="Specify instructions for the AI to follow after it executes this workflow."
													/>
													<Form.Item {...restField} name={[name, 'postExecutionPrompt']}>
														<TextArea
															placeholder="e.g., After sharing the MacBook details, ask if they would like to know about financing options or if they have any other questions about the specifications."
															autoSize={{ minRows: 2, maxRows: 4 }}
															className="w-full"
														/>
													</Form.Item>
												</div>
											</>
										)}
										<div className="my-4">
											<LabelWithDescription
												label={
													<div className="flex items-center">
														<span className="text-sm font-semibold">Timeout (Seconds)</span>
													</div>
												}
												customClassName="text-sm"
												description="A longer timeout might delay data loading and cause latency during calls."
											/>
											<span className="mb-4">
												<Form.Item
													className="flex-auto !mb-0"
													name={[name, 'timeoutSeconds']}
													rules={[{ required: true, message: 'Required' }]}
													normalize={(value) =>
														typeof value === 'string' ? parseInt(value, 10) : value
													}
												>
													<InputNumber min={2} max={30} defaultValue={10} className="w-full" />
												</Form.Item>
											</span>
										</div>
									</div>
								</div>
							);
						})}
						{showAddFieldButton && (
							<Form.Item>
								<Button
									type="dashed"
									onClick={() => handleAddApi(add)}
									block
									icon={<PlusOutlined />}
								>
									Add Another API Workflow
								</Button>
							</Form.Item>
						)}
					</>
				)}
			</Form.List>
		</>
	);

	return (
		<>
			<Spin
				spinning={mutation.isPending}
				indicator={<LoadingOutlined style={{ fontSize: '48px' }} spin />}
			>
				<OverViewCardForm
					form={form}
					initialValues={call_webhook_workflows}
					formItems={<FormItems />}
					handleSubmit={handleSubmit}
					isFieldTouchReset={isFieldTouchReset}
					hideUnsavedChangesModal={isDuplicate}
				/>
			</Spin>
		</>
	);
};

export default ApiWorkflowForm;
